import { useEffect, useState } from "react";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import { Pencil, Trash } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useParams } from "react-router";
import { Rol as RolEnum } from "@/types/roles";
import { apiFetch } from "@/service/api";

interface Rol {
  id: number;
  nombre: string;
  descripcion: string;
}
interface ListarRolesProps {
  onEdit: (rol: Rol) => void;
}

export default function ListarRoles({ onEdit }: ListarRolesProps) {
  const { id: proyecto_id } = useParams();
  const { user } = useAuth();
  const [roles, setRoles] = useState<Rol[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rolToDelete, setRolToDelete] = useState<number | null>(null);
  const rolId: number = user.rol;
  const canDelete =
    rolId !== RolEnum.COLABORADOR && rolId !== RolEnum.ASISTENTE;

  const fetchRoles = async () => {
    try {
      const response = await apiFetch(
        `roles-proyectos/find-proyecto/${proyecto_id}`,
      );
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [proyecto_id]);
  const handleDelete = (id: number) => {
    setRolToDelete(id);
    setIsModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!rolToDelete) return;
    try {
      const response = await apiFetch(`roles-proyectos/remove/${rolToDelete}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setRoles(roles.filter((r) => r.id !== rolToDelete));
        setIsModalOpen(false);
        setRolToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting rol:", error);
    }
  };
  const truncateText = (text: string) =>
    text.length > 35 ? text.substring(0, 35) + "..." : text;
  const columns: Column<Rol>[] = [
    {
      label: "Nombre",
      key: "nombre",
      render: (value: any) => truncateText(String(value || "")),
    },
    {
      label: "Descripcion",
      key: "descripcion",
      render: (value: any) => truncateText(String(value || "")),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () => onEdit(row),
            },
            ...(canDelete
              ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="min-h- rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-6 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Roles de Proyectos
        </h3>
        {loading ? (
          <div className="flex items-center justify-center p-10">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
          </div>
        ) : roles.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              No hay roles registrados.
            </p>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={roles} />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center justify-center w-12 h-12 mb-4 bg-red-100 rounded-full dark:bg-red-500/20">
            <Trash className="text-red-600 size-6 dark:text-red-500" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h4>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar este rol?
          </p>
          <div className="flex justify-center w-full gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="w-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
